import AddressVerificationProvider from '../addressVerification/AddressVerificationProvider';
import FormHandler, { FormHandlerOnSubmitOptions } from '../forms/FormHandler';
import StateFilteredAddress from '../forms/address/StateFilteredAddress';
import { Address, StateFilterData } from '../forms/address/types';
import { IRecaptchaSettings, RecaptchaService } from '../utils/RecaptchaService';

class AccountCreateUpdateFormHandler {
  recaptchaService: RecaptchaService;

  form: FormHandler;

  addressForm: StateFilteredAddress | null;

  avsProvider: AddressVerificationProvider | null;

  constructor(
    private $form: HTMLFormElement,
    isAddressVerificationEnabled: boolean,
    stateFilterConfig: StateFilterData,
    private isAddressSectionDisplayed: boolean,
    private recaptchaSettings: IRecaptchaSettings,
    primaryAddress: Address | null,
  ) {
    this.recaptchaService = new RecaptchaService($form, recaptchaSettings);

    this.form = new FormHandler($form, {
      onValidationSuccess: this.onSubmit,
    });

    if (this.isAddressSectionDisplayed) {
      this.addressForm = new StateFilteredAddress(this.form, {
        countries: stateFilterConfig.countries,
        states: stateFilterConfig.states,
        fields: {
          Street1: 'Address.Address1',
          Street2: 'Address.Address2',
          CountryId: 'Address.CountryId',
          StateId: 'Address.StateId',
          PostalCode: 'Address.PostalCode',
          City: 'Address.City',
        },
      });

      this.avsProvider = new AddressVerificationProvider(
        this.addressForm,
        primaryAddress,
        isAddressVerificationEnabled,
      );
    } else {
      this.addressForm = null;
      this.avsProvider = null;
    }
  }

  onSubmit = async ({ stopSubmitting }: FormHandlerOnSubmitOptions) => {
    const {
      $form, avsProvider, addressForm,
    } = this;

    // 1) Always enforce reCAPTCHA when enabled:
    // - v2: user must complete checkbox
    // - v3: execute() and write token to hidden input
    if (this.recaptchaSettings.isRecaptchaEnabled) {
      try {
        const token = await this.recaptchaService.challenge('signup');
        if (token === undefined) {
          stopSubmitting();
          return;
        }
      } catch (err) {
        stopSubmitting();
        return;
      }
    }

    if (this.testShouldVerifyAddress()) {
      const response = await avsProvider?.verifyAddress();

      if (response) {
        if (response.wasCancelled) {
          response.modal.close();
          stopSubmitting();
          return;
        }

        addressForm?.updateAddress(response.confirmedAddress);
      }
    }

    $form.submit();
  };

  testShouldVerifyAddress() {
    const { avsProvider, isAddressSectionDisplayed } = this;

    if (!isAddressSectionDisplayed) return false;
    if (!avsProvider) return false;

    return avsProvider.testCanVerifyAddress();
  }
}

export default AccountCreateUpdateFormHandler;
